import {
  Activity,
  Calendar,
  KeyCommand,
  List,
  MessageText,
  PathArrow,
  ProfileCircle,
  Reports,
  SelectWindow,
  Settings,
} from "iconoir-react";
import {
  BroadcastManyQuery,
  EnumOrganizationMetadataDisabledApps,
  BroadcastByUserQuery,
  BroadcastsByIdsStatsForSingleUserQuery,
} from "gql/generated";
import { Token } from "styled-system/tokens";

export interface AppCardProps {
  Icon: React.ForwardRefExoticComponent<
    Omit<React.SVGProps<SVGSVGElement>, "ref"> &
      React.RefAttributes<SVGSVGElement>
  >;
  title: string;
  description: string;
  route: string;
  key: EnumOrganizationMetadataDisabledApps;
  iconColor: Token;
  /** If set, the app card is hidden unless this feature flag is enabled. */
  featureFlagKey?: string;
}

export const appsConfig: AppCardProps[] = [
  {
    key: EnumOrganizationMetadataDisabledApps.Directory,
    Icon: KeyCommand,
    title: "home:APPS.DIRECTORY.TITLE",
    description: "home:APPS.DIRECTORY.DESCRIPTION",
    route: "/directory",
    iconColor: "colors.icon.accent.purple.mid",
  },
  {
    key: EnumOrganizationMetadataDisabledApps.People,
    Icon: ProfileCircle,
    title: "home:APPS.PEOPLE.TITLE",
    description: "home:APPS.PEOPLE.DESCRIPTION",
    route: "/people",
    iconColor: "colors.icon.success.mid",
  },
  {
    key: EnumOrganizationMetadataDisabledApps.CustomLists,
    Icon: List,
    title: "home:APPS.CUSTOM_LISTS.TITLE",
    description: "home:APPS.CUSTOM_LISTS.DESCRIPTION",
    route: "/custom-lists",
    iconColor: "colors.icon.warning.mid",
  },
  {
    key: EnumOrganizationMetadataDisabledApps.Events,
    Icon: Calendar,
    title: "home:APPS.EVENTS.TITLE",
    description: "home:APPS.EVENTS.DESCRIPTION",
    route: "/events",
    iconColor: "colors.icon.error.mid",
  },
  {
    key: EnumOrganizationMetadataDisabledApps.Tripper,
    Icon: PathArrow,
    title: "home:APPS.TRIPPER.TITLE",
    description: "home:APPS.TRIPPER.DESCRIPTION",
    route: "/tripper",
    iconColor: "colors.icon.highlight.mid",
    featureFlagKey: "tripper-enabled",
  },
  {
    key: EnumOrganizationMetadataDisabledApps.Widgets,
    Icon: SelectWindow,
    title: "home:APPS.WIDGETS.TITLE",
    description: "home:APPS.WIDGETS.DESCRIPTION",
    route: "/widgets",
    iconColor: "colors.icon.accent.blue.mid",
  },
];

export enum ViewKey {
  ActivityFeed = "ACTIVITY_FEED",
  Broadcast = "BROADCAST",
  Stats = "STATS",
  Settings = "SETTINGS",
}

export interface ViewProps {
  Icon: React.ForwardRefExoticComponent<
    Omit<React.SVGProps<SVGSVGElement>, "ref"> &
      React.RefAttributes<SVGSVGElement>
  >;
  title: string;
  description: string;
  key: ViewKey;
}

export const viewsConfig: ViewProps[] = [
  {
    key: ViewKey.ActivityFeed,
    Icon: Activity,
    title: "home:VIEWS.ACTIVITY_FEED.TITLE",
    description: "home:VIEWS.ACTIVITY_FEED.DESCRIPTION",
  },
  {
    key: ViewKey.Broadcast,
    Icon: MessageText,
    title: "home:VIEWS.BROADCAST.TITLE",
    description: "home:VIEWS.BROADCAST.DESCRIPTION",
  },
  {
    key: ViewKey.Stats,
    Icon: Reports,
    title: "home:VIEWS.STATS.TITLE",
    description: "home:VIEWS.STATS.DESCRIPTION",
  },
  {
    key: ViewKey.Settings,
    Icon: Settings,
    title: "home:VIEWS.SETTINGS.TITLE",
    description: "home:VIEWS.SETTINGS.DESCRIPTION",
  },
];

export type BroadcastStatus =
  | "VIEWED"
  | "NOT_VIEWED"
  | "IN_PROGRESS"
  | "COMPLETED"
  | "DOWNLOADED"
  | "PLAYED"
  | "FORM_DRAFT"
  | "FORM_SUBMITTED"
  | "FORM_PENDING_REVIEW"
  | "FORM_REJECTED";

export const getBroadcastStatus = (
  broadcast:
    | BroadcastManyQuery["broadcastMany"][0]
    | BroadcastByUserQuery["broadcastByUser"][0],
  broadcastStats: BroadcastsByIdsStatsForSingleUserQuery["broadcastsByIdsStatsForSingleUser"],
) => {
  const broadcastStat = broadcastStats?.find(
    b => b.broadcastId === broadcast._id,
  );
  const isSingleResource =
    broadcast.resources.length === 1 && broadcast.resources[0];

  if (isSingleResource) {
    return getResourceStatus(broadcast.resources[0]?._id, broadcastStat);
  }

  return broadcastStat?.viewed ? "VIEWED" : "NOT_VIEWED";
};

export const getResourceStatus = (
  resourceId: string,
  broadcastStat?: BroadcastsByIdsStatsForSingleUserQuery["broadcastsByIdsStatsForSingleUser"][0],
) => {
  const resourceStat = broadcastStat?.resources?.find(
    r => r.resourceId === resourceId,
  );
  if (resourceStat) {
    return resourceStat.completed
      ? "COMPLETED"
      : resourceStat.inProgress
        ? "IN_PROGRESS"
        : resourceStat.downloaded
          ? "DOWNLOADED"
          : resourceStat.played
            ? "PLAYED"
            : resourceStat.viewed || broadcastStat?.viewed
              ? "VIEWED"
              : "NOT_VIEWED";
  }
  return "NOT_VIEWED";
};
